
import { Doctor } from '../types';
import { 
  getMockDoctors, 
  saveMockDoctors, 
  resetMockDoctors,
  ensureDoctorDefaults
} from '../utils/mockStorageUtils';
import { extractDoctorFromFormData } from '../utils/formDataUtils';

/**
 * Mock implementation of doctor service for development environments
 */
export const doctorServiceMock = {
  resetAll: async (): Promise<void> => {
    resetMockDoctors();
  },
  
  getAll: async (): Promise<Doctor[]> => {
    const doctors = getMockDoctors();
    // Ensure all doctors have the required fields
    return doctors.map(doctor => ensureDoctorDefaults(doctor));
  },
  
  getById: async (id: number): Promise<Doctor> => {
    const doctors = getMockDoctors();
    const doctor = doctors.find(doc => doc.id === id);
    
    if (!doctor) {
      throw new Error('Doctor not found');
    }
    
    return ensureDoctorDefaults(doctor);
  },
  
  create: async (doctorData: FormData | Omit<Doctor, 'id'>): Promise<Doctor> => {
    const doctors = getMockDoctors();
    const newId = doctors.length > 0 ? Math.max(...doctors.map(d => d.id)) + 1 : 1;
    
    let newDoctor: Doctor;
    
    if (doctorData instanceof FormData) {
      // Extract data from FormData
      newDoctor = {
        id: newId,
        ...extractDoctorFromFormData(doctorData)
      } as Doctor;
    } else {
      // Regular object data
      newDoctor = {
        id: newId,
        ...(doctorData as Omit<Doctor, 'id'>)
      };
    }
    
    // Ensure all required fields
    newDoctor = ensureDoctorDefaults(newDoctor);
    
    doctors.push(newDoctor);
    saveMockDoctors(doctors);
    
    return newDoctor;
  },
  
  update: async (id: number, doctorData: FormData | Partial<Doctor>): Promise<Doctor> => {
    const doctors = getMockDoctors();
    const index = doctors.findIndex(d => d.id === id);
    
    if (index === -1) {
      throw new Error('Doctor not found');
    }
    
    let updatedDoctor = { ...doctors[index] };
    
    if (doctorData instanceof FormData) {
      // Extract data from FormData and merge with existing doctor
      updatedDoctor = {
        ...updatedDoctor,
        ...extractDoctorFromFormData(doctorData, updatedDoctor)
      };
    } else {
      // Regular object data
      updatedDoctor = {
        ...updatedDoctor,
        ...(doctorData as Partial<Doctor>)
      };
    }
    
    // Ensure all required fields have values
    updatedDoctor = ensureDoctorDefaults(updatedDoctor);
    
    doctors[index] = updatedDoctor;
    saveMockDoctors(doctors);
    
    return updatedDoctor;
  },
  
  delete: async (id: number): Promise<void> => {
    const doctors = getMockDoctors();
    const filteredDoctors = doctors.filter(d => d.id !== id);
    
    if (doctors.length === filteredDoctors.length) {
      throw new Error('Doctor not found');
    }
    
    saveMockDoctors(filteredDoctors);
  }
};
